/**
 * Native Interactive Site Map Feature Entry Point.
 *
 * Responsibilities:
 * - Inject Redux slices into the store
 * - Create and bootstrap the plugin host
 * - Manage map library loading and plugin lifecycle
 * - Export Redux actions and selectors for use in components
 */

import { epic$, store, store$ } from '@redux/store';
import { ReplaySubject, Subject, fromEvent } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import LoadingStatus from 'enums/loadingStatus';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import {
  selectLoading,
  selectConfigurationResourceDomain,
  selectConfigurationImageResizeService,
  selectMapLibraryStatus,
  selectLayoutBreakpoint,
} from 'layout/redux/selectors';
import createMapLibraryPlugin from 'libraries/mapLibrary.plugin';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { createModalService } from 'layout/services/modalService';
import createModal from 'layout/components/modal';
import drawerService from 'layout/services/drawerService';
import createInteractiveSiteMapModalHandler, { MODAL_ID, createModalInformationStore } from './handlers/modalHandler';
import { createMapLibraryLifecycleHandlers, getMapSourceUrl } from './handlers/mapLibraryLifecycleHandlers';
import createDrawerLifecycleHandlers from './handlers/drawerLifecycleHandlers';
import createMapControlsPlugin from './plugins/mapTypeToggle.plugin';
import createInteractiveSiteMapDrawerPlugin, { DRAWER_ID } from './plugins/drawer.plugin';
import createMapPlugin from './plugins/map.plugin';
import createPluginHost from './plugins/app';
import createRenderCommunitySiteMapPlugin from './plugins/renderCommunitySiteMap.plugin';
import createLotInteractionPlugin from './plugins/lotInteraction.plugin';
import createSiteMapViewportPlugin from './plugins/siteMapViewport.plugin';
import createMapChipsPlugin from './plugins/mapChips.plugin';
import createListViewPlugin from './plugins/listView.plugin';
import createLotMapSelectionPlugin from './plugins/lotMapSelection.plugin';
import createFloorPlanDetailPlugin from './plugins/floorPlanDetail.plugin';
import createHousingUnitDetailPlugin from './plugins/housingUnitDetail.plugin';
import createCardLotHighlightPlugin from './plugins/cardLotHighlight.plugin';
import createTrackingPlugin from './plugins/tracking.plugin';
import createImageErrorPlugin from './plugins/imageError.plugin';
import createMobileLotCardPlugin from './plugins/mobileLotCard.plugin';
import { createNativeInteractiveSiteMapDataSlice, DATA_SLICE_KEY } from './redux/dataSlice';

/**
 * Creates the modal and orchestrates Interactive Site Map initialization
 */
export default function setupNativeInteractiveSiteMap({
  communityId,
  communityName,
  marketId,
  marketName,
  brandName,
  latitude,
  longitude,
  mapLibrarySlice,
} = {}) {
  const appRoot = document.querySelector('[data-native-interactive-site-map]');
  const triggerButton = document.querySelector('[data-show-native-interactive-site-map]');

  if (!appRoot && !triggerButton) {
    return;
  }

  // Inject Redux slices
  const dataSlice = createNativeInteractiveSiteMapDataSlice();
  store.injectReducer(DATA_SLICE_KEY, dataSlice.reducer);

  const modalInformationStore = createModalInformationStore();

  const interactiveSiteMapModalService = createModalService();
  createModal({
    modalService: interactiveSiteMapModalService,
    container: document.getElementById(MODAL_ID),
    getModalInformation: modalInformationStore.get,
    clearModalInformation: modalInformationStore.clear,
    overlayId: 'nativeInteractiveSiteMapOverlayElement',
  });

  // A ReplaySubject(1) that emits once when runtime configuration becomes available.
  const configuration$ = new ReplaySubject(1);
  store$.pipe(
    filter(state => selectLoading(state) === LoadingStatus.Succeeded.toString()),
    map(state => ({
      resourceDomain: selectConfigurationResourceDomain(state),
      imageResizeService: selectConfigurationImageResizeService(state),
    })),
    take(1),
  ).subscribe(configuration$);

  // A ReplaySubject(1) that emits once when the map library has loaded successfully.
  const mapLibraryLoaded$ = new ReplaySubject(1);
  store$.pipe(
    map(selectMapLibraryStatus),
    filter(status => status === LoadingStatus.Succeeded.toString()),
    take(1),
  ).subscribe(mapLibraryLoaded$);

  const mapSourceElement = document.querySelector('[data-maps-script]');
  const mapSource = getMapSourceUrl(mapSourceElement);

  const mapLibraryLifecycle = createMapLibraryLifecycleHandlers(
    store.dispatch.bind(store),
    mapLibrarySlice.actions,
  );

  const drawerLifecycle = createDrawerLifecycleHandlers(
    store.dispatch.bind(store),
    uiStateSlice.actions,
    DRAWER_ID
  );

  let host = null;
  let beforeUnloadHandler = null;

  async function stopHost() {
    if (!host || typeof host.stop !== 'function') {
      return;
    }

    if (beforeUnloadHandler) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      beforeUnloadHandler = null;
    }

    await host.stop();
    host = null;
  }

  async function bootstrapNativeInteractiveSiteMap(rootElement) {
    // Fresh per bootstrap (not shared across mobile drawer open/close cycles like
    // configuration$/mapLibraryLoaded$), so a reopen never replays a stale/detached
    // svgElement from a previous session.
    const svgOverlayReady$ = new ReplaySubject(1);

    // Plain Subject, not ReplaySubject — hover is "now or nothing", a late subscriber has nothing to catch up on.
    const cardLotHovered$ = new Subject();

    // Emitted before mapBackgroundClicked is dispatched, so subscribers still see the
    // pre-navigation view state (see lotInteraction.plugin.js#onMapBackgroundClicked).
    const mapBackgroundClicked$ = new Subject();

    host = createPluginHost({
      root: rootElement,
      services: {
        epic$,
        store$,
        dispatch: store.dispatch.bind(store),
        getState: store.getState.bind(store),
        configuration$,
        drawerService,
        dataActions: dataSlice.actions,
        mapLibraryActions: mapLibrarySlice.actions,
        mapLibraryLoaded$,
        svgOverlayReady$,
        cardLotHovered$,
        mapBackgroundClicked$,
      },
      state: {
        community: { communityId, communityName, marketId, marketName, brandName, latitude, longitude },
        isMobile: selectLayoutBreakpoint(store.getState()) === LayoutBreakpoint.Small,
      },
      plugins: [
        createMapLibraryPlugin({
          provider: 'google',
          config: {
            src: mapSource,
            onScriptInjected: mapLibraryLifecycle.onMapLibraryFetch,
            onLoad: mapLibraryLifecycle.onMapLibraryLoaded,
            onError: mapLibraryLifecycle.onMapLibraryError,
          },
        }),
        createMapPlugin(),
        createMapControlsPlugin(),
        createRenderCommunitySiteMapPlugin(),
        createLotInteractionPlugin(),
        createSiteMapViewportPlugin(),
        createMapChipsPlugin(),
        createListViewPlugin(),
        createLotMapSelectionPlugin(),
        createFloorPlanDetailPlugin(),
        createHousingUnitDetailPlugin(),
        createCardLotHighlightPlugin(),
        createImageErrorPlugin(),
        createTrackingPlugin(),
        createMobileLotCardPlugin(),
        createInteractiveSiteMapDrawerPlugin(),
      ],
    });

    await host.init();
    await host.start();

    beforeUnloadHandler = () => stopHost();
    window.addEventListener('beforeunload', beforeUnloadHandler);
  }

  function createInteractiveSiteMapModal() {
    const modalTemplate = document.querySelector('[data-native-interactive-site-map-modal-template]');

    if (host || !modalTemplate) {
      return;
    }

    createInteractiveSiteMapModalHandler({
      appRoot: modalTemplate,
      modalService: interactiveSiteMapModalService,
      setModalInformation: modalInformationStore.set,
      onReady: drawerLifecycle.onDrawerReady,
      onBootstrap: bootstrapNativeInteractiveSiteMap,
      onStop: async () => {
        drawerLifecycle.onDrawerClosed();
        store.dispatch(dataSlice.actions.viewReset());
        await stopHost();
      },
    });
  }

  if (triggerButton) {
    fromEvent(triggerButton, 'click').subscribe(createInteractiveSiteMapModal);
    return;
  }

  (async () => {
    await bootstrapNativeInteractiveSiteMap(appRoot);
  })();
}

// This feature is not responsible for tracking Segment nor GoogleAnalytics (or any other analytics).
// However, it exports the raw interaction Subjects, so the interested parties which own the actual dispatch
// of analytics events can subscribe to the Subjects and build their own events from the raw interaction data.
export {
  mapInteractionOccurred$,
  panelUpdatedOccurred$,
  panelScrollOccurred$,
  drawerDraggedOccurred$,
  lotSelectedOccurred$,
  cardsScrolledOccurred$,
  tabSelectedOccurred$,
  notifyWhenAvailableClickedOccurred$,
  floorPlanViewedOccurred$,
  viewFloorPlanClickedOccurred$,
  emptyConfigViewedOccurred$,
  similarHomesToggledOccurred$,
  similarHomeCardClickedOccurred$,
  soldSeeAllClickedOccurred$,
  detailViewOpenedOccurred$,
  detailTabClicked$,
  detailsTabSelectedOccurred$,
  changeLotSelectedOccurred$,
  exteriorSelectedOccurred$,
  exteriorSelectorExpandedOccurred$,
  mobileLotMapClickedOccurred$,
  mobileLotCardNavigationOccurred$,
} from './redux/observables';

export {
  buildMapInteractionEvent as buildSegmentMapInteractionEvent,
  buildPanelUpdatedEvent as buildSegmentPanelUpdatedEvent,
  buildPanelScrollEvent as buildSegmentPanelScrollEvent,
  buildDrawerDraggedEvent as buildSegmentDrawerDraggedEvent,
  buildLotSelectedEvent as buildSegmentLotSelectedEvent,
  buildCardsScrolledEvent as buildSegmentCardsScrolledEvent,
  buildTabSelectedEvent as buildSegmentTabSelectedEvent,
  buildNotifyWhenAvailableClickedEvent as buildSegmentNotifyWhenAvailableClickedEvent,
  buildFloorPlanViewedEvent as buildSegmentFloorPlanViewedEvent,
  buildViewFloorPlanClickedEvent as buildSegmentViewFloorPlanClickedEvent,
  buildEmptyConfigViewedEvent as buildSegmentEmptyConfigViewedEvent,
  buildSimilarHomesToggledEvent as buildSegmentSimilarHomesToggledEvent,
  buildSimilarHomeCardClickedEvent as buildSegmentSimilarHomeCardClickedEvent,
  buildSoldSeeAllClickedEvent as buildSegmentSoldSeeAllClickedEvent,
  buildDetailViewOpenedEvent as buildSegmentDetailViewOpenedEvent,
  buildDetailsTabSelectedEvent as buildSegmentDetailsTabSelectedEvent,
  buildChangeLotSelectedEvent as buildSegmentChangeLotSelectedEvent,
  buildExteriorSelectedEvent as buildSegmentExteriorSelectedEvent,
  buildExteriorSelectorExpandedEvent as buildSegmentExteriorSelectorExpandedEvent,
  buildMobileLotMapClickedEvent as buildSegmentMobileLotMapClickedEvent,
  buildMobileLotCardNavigationEvent as buildSegmentMobileLotCardNavigationEvent,
} from './services/segmentEventBuilder';

export {
  buildMapInteractionEvent as buildGoogleAnalytics4MapInteractionEvent,
  buildPanelUpdatedEvent as buildGoogleAnalytics4PanelUpdatedEvent,
  buildPanelScrollEvent as buildGoogleAnalytics4PanelScrollEvent,
  buildDrawerDraggedEvent as buildGoogleAnalytics4DrawerDraggedEvent,
  buildLotSelectedEvent as buildGoogleAnalytics4LotSelectedEvent,
  buildCardsScrolledEvent as buildGoogleAnalytics4CardsScrolledEvent,
  buildTabSelectedEvent as buildGoogleAnalytics4TabSelectedEvent,
  buildNotifyWhenAvailableClickedEvent as buildGoogleAnalytics4NotifyWhenAvailableClickedEvent,
  buildFloorPlanViewedEvent as buildGoogleAnalytics4FloorPlanViewedEvent,
  buildViewFloorPlanClickedEvent as buildGoogleAnalytics4ViewFloorPlanClickedEvent,
  buildEmptyConfigViewedEvent as buildGoogleAnalytics4EmptyConfigViewedEvent,
  buildSimilarHomesToggledEvent as buildGoogleAnalytics4SimilarHomesToggledEvent,
  buildSimilarHomeCardClickedEvent as buildGoogleAnalytics4SimilarHomeCardClickedEvent,
  buildSoldSeeAllClickedEvent as buildGoogleAnalytics4SoldSeeAllClickedEvent,
  buildDetailViewOpenedEvent as buildGoogleAnalytics4DetailViewOpenedEvent,
  buildDetailsTabSelectedEvent as buildGoogleAnalytics4DetailsTabSelectedEvent,
  buildChangeLotSelectedEvent as buildGoogleAnalytics4ChangeLotSelectedEvent,
  buildExteriorSelectedEvent as buildGoogleAnalytics4ExteriorSelectedEvent,
  buildExteriorSelectorExpandedEvent as buildGoogleAnalytics4ExteriorSelectorExpandedEvent,
  buildMobileLotMapClickedEvent as buildGoogleAnalytics4MobileLotMapClickedEvent,
  buildMobileLotCardNavigationEvent as buildGoogleAnalytics4MobileLotCardNavigationEvent,
} from './services/googleAnalytics4EventBuilder';
